import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '@/api/client';
import { useAuthContext } from '@/hooks/useAuthContext';
import { usePortalMagicLink } from '@/hooks/usePortalMagicLink';
import { getApiErrorMessage } from '@/lib/utils';
import type { AuthUser } from '@/types';
import logo from '@/assets/logo.png';

// 'linkSent' is the magic-link sibling of 'otp': the server told us it sent a
// tokened login link instead of a code, so there is nothing to type — the
// customer finishes in WhatsApp.
type Step = 'phone' | 'otp' | 'linkSent';

const OTP_COOLDOWN_SECONDS = 60;

const PhoneCircleIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
  </svg>
);

function toAuthUser(user: {
  id: string;
  role: string;
  username: string;
  name?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  createdAt?: string;
}): AuthUser {
  return {
    id: user.id,
    role: user.role as AuthUser['role'],
    username: user.username,
    name: user.name ?? null,
    phone: user.phone ?? null,
    avatarUrl: user.avatarUrl ?? null,
    createdAt: user.createdAt ?? '',
  };
}

export default function PortalJoinPage() {
  const { jobId } = useParams<{ jobId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUserFromPortal } = useAuthContext();
  // Only consulted where the server response can't tell us the mode (429).
  const magicLinkEnabled = usePortalMagicLink();

  // Magic-link auto-exchange: a tokened URL (?t=...) logs in with one tap.
  const token = searchParams.get('t');
  const canExchange = Boolean(token && jobId);
  const [exchanging, setExchanging] = useState(canExchange);

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // Countdown timer for resend (shared by the OTP and link-sent steps)
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Exchange the link token for a session on arrival. Guarded by the
  // `exchanging` STATE (not a ref): StrictMode's dev-only effect replay
  // (setup -> cleanup -> setup) must re-run the attempt — a persistent ref
  // would block the second setup while the first was already cancelled,
  // stranding the spinner forever. The duplicate dev POST this allows is
  // harmless: the exchange is idempotent by design (multi-use token). Every
  // failure falls through to the manual form with a request-a-new-link notice.
  useEffect(() => {
    if (!canExchange || !exchanging) return;
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.post(
          '/auth/portal-link-exchange',
          { jobId, token },
          { signal: controller.signal },
        );
        if (cancelled) return;
        setUserFromPortal(toAuthUser(res.data.user));
        navigate(`/portal/${jobId}`, { replace: true });
      } catch {
        if (cancelled) return;
        setExchanging(false);
        // Strip the dead token from the URL so a refresh lands on the form
        // instead of replaying the doomed spinner-then-fail cycle.
        navigate(jobId ? `/portal/join/${jobId}` : '/portal/join', { replace: true });
        setError(
          'This link is invalid or has expired. Enter your phone number to get a new one. / Link tak sah atau dah tamat tempoh - masukkan no. telefon untuk dapatkan yang baru.',
        );
      }
    })();
    return () => {
      cancelled = true;
      // Abort the in-flight request, not just ignore its response: a completed
      // exchange sets the customer cookie via Set-Cookie whether or not JS
      // reads the response, so a signed-in staff user backing out mid-exchange
      // would keep staff UI state over a silently-swapped customer session.
      // (Abort can't recall a response that already arrived — that residual
      // window is unavoidable client-side and resolved by the next /auth/me.)
      controller.abort();
    };
  }, [canExchange, exchanging, jobId, token, navigate, setUserFromPortal]);

  const requestAccess = useCallback(
    async (isResend = false) => {
      if (!phone.trim()) return;
      setLoading(true);
      setError(null);
      try {
        // The RESPONSE decides the mode (authoritative, no flag drift): the
        // server sends either an OTP or a tokened WhatsApp link. Applied on
        // resends too — a flag flip mid-wait (kill switch) must move the
        // customer to the screen that matches what was actually sent.
        const res = await apiClient.post('/auth/portal-join', { phone: phone.trim() });
        setCooldown(OTP_COOLDOWN_SECONDS);
        setStep(res.data?.status === 'LINK_SENT' ? 'linkSent' : 'otp');
      } catch (err: any) {
        const resp = err?.response;
        if (resp?.status === 404) {
          setError('Phone number not found. Please contact the workshop.');
        } else if (resp?.status === 429) {
          const details = resp?.data?.error?.details;
          const retryAfter = details?.retryAfter;
          if (retryAfter) {
            setCooldown(retryAfter);
            if (details?.mode) {
              // Server-authoritative (persisted with the send that armed this
              // cooldown) — applied on resends too: another tab/device may
              // have just sent the OPPOSITE mode, and this 429 is how the
              // stale tab finds out (e.g. an OTP screen after the other tab
              // received a link has no usable code to enter).
              setStep(details.mode === 'LINK' ? 'linkSent' : 'otp');
            } else if (!isResend) {
              // Legacy API without details.mode: config-flag GUESS, initial
              // submit only — a guess must never yank an established waiting
              // screen (fail-closed false during a config outage would drag a
              // correct linkSent screen to a phantom OTP one).
              setStep(magicLinkEnabled ? 'linkSent' : 'otp');
            }
          }
          setError(resp?.data?.error?.message || 'Please wait before requesting again.');
        } else {
          setError(getApiErrorMessage(err));
        }
      } finally {
        setLoading(false);
      }
    },
    [phone, magicLinkEnabled],
  );

  async function handlePhoneSubmit(e: React.FormEvent) {
    e.preventDefault();
    await requestAccess(false);
  }

  async function handleVerifySubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!otp.trim() || !phone.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.post('/auth/portal-verify', {
        phone: phone.trim(),
        otp: otp.trim(),
      });
      setUserFromPortal(toAuthUser(res.data.user));
      navigate(jobId ? `/portal/${jobId}` : '/portal', { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function handleBackToPhone() {
    setStep('phone');
    setOtp('');
    setError(null);
  }

  // Auto-exchange in flight: no blank flash, no form — just the sign-in state.
  if (exchanging) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-sm flex flex-col items-center">
          <img
            src={logo}
            alt="BRO Autopaint Garage"
            className="h-[72px] w-[72px] rounded-2xl object-cover mb-6 shadow-lg"
          />
          <div
            className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent mb-4"
            role="status"
            aria-label="Signing in"
          />
          <p className="text-sm font-medium text-foreground">Signing you in...</p>
          <p className="text-xs text-muted-foreground mt-1">Sedang log masuk...</p>
        </div>
        <p className="text-[11px] text-muted-foreground mt-8">Powered by BengkelOS</p>
      </div>
    );
  }

  // Magic-link sent: the customer finishes in WhatsApp — nothing to type here.
  if (step === 'linkSent') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-sm flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4 text-blue-600 dark:text-blue-400">
            <PhoneCircleIcon />
          </div>

          <h1 className="text-lg font-bold text-foreground mb-1 text-center">
            Check Your WhatsApp
          </h1>
          <p className="text-sm text-muted-foreground mb-1 text-center">Semak WhatsApp anda</p>
          <p className="text-xs text-muted-foreground mb-6 text-center leading-relaxed">
            We sent your portal link to <span className="font-medium text-foreground">{phone}</span>
            . Tap the link in WhatsApp to sign in - no code needed.
          </p>

          {error && <p className="text-destructive text-sm text-center mb-4">{error}</p>}

          <button
            type="button"
            onClick={() => requestAccess(true)}
            disabled={loading || cooldown > 0}
            className="w-full text-sm text-blue-600 hover:text-blue-700 disabled:text-muted-foreground py-3 min-h-[44px]"
          >
            {loading ? 'Sending...' : cooldown > 0 ? `Resend link in ${cooldown}s` : 'Resend Link'}
          </button>

          <button
            type="button"
            onClick={handleBackToPhone}
            className="w-full text-sm text-muted-foreground hover:text-foreground py-3 min-h-[44px]"
          >
            &#8592; Back
          </button>
        </div>

        <p className="text-[11px] text-muted-foreground mt-8">Powered by BengkelOS</p>
      </div>
    );
  }

  // Step 2: OTP Verification
  if (step === 'otp') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6">
        <div className="w-full max-w-sm flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4 text-blue-600 dark:text-blue-400">
            <PhoneCircleIcon />
          </div>

          <h1 className="text-lg font-bold text-foreground mb-1 text-center">Verify Your Phone</h1>
          <p className="text-sm text-muted-foreground mb-1 text-center">
            Sahkan nombor telefon anda
          </p>
          <p className="text-xs text-muted-foreground mb-6 text-center leading-relaxed">
            We sent a 6-digit code to <span className="font-medium text-foreground">{phone}</span>
          </p>

          <form onSubmit={handleVerifySubmit} className="w-full space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground/70 mb-1.5">
                OTP Code
                <span className="text-muted-foreground font-normal ml-1">/ Kod pengesahan</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full border border-border rounded-xl px-4 py-3 text-center text-lg font-mono tracking-widest bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[48px]"
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </div>

            {error && <p className="text-destructive text-sm text-center">{error}</p>}

            <button
              type="submit"
              disabled={loading || otp.length < 6}
              className="w-full bg-blue-600 text-white py-3.5 rounded-[14px] text-[15px] font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-lg shadow-blue-600/30 min-h-[48px]"
            >
              {loading ? 'Verifying...' : 'Verify & Continue'}
            </button>

            <button
              type="button"
              onClick={() => requestAccess(true)}
              disabled={loading || cooldown > 0}
              className="w-full text-sm text-blue-600 hover:text-blue-700 disabled:text-muted-foreground py-3 min-h-[44px]"
            >
              {cooldown > 0 ? `Resend OTP in ${cooldown}s` : 'Resend OTP'}
            </button>

            <button
              type="button"
              onClick={handleBackToPhone}
              className="w-full text-sm text-muted-foreground hover:text-foreground py-3 min-h-[44px]"
            >
              &#8592; Back
            </button>
          </form>
        </div>

        <p className="text-[11px] text-muted-foreground mt-8">Powered by BengkelOS</p>
      </div>
    );
  }

  // Step 1: Phone Entry / Landing
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm flex flex-col items-center">
        <img
          src={logo}
          alt="BRO Autopaint Garage"
          className="h-[72px] w-[72px] rounded-2xl object-cover mb-5 shadow-lg"
        />

        <h1 className="text-[22px] font-extrabold text-foreground mb-1 text-center">
          BRO Autopaint Garage
        </h1>
        <p className="text-[13px] text-muted-foreground mb-8 text-center">Shah Alam, Selangor</p>

        <form onSubmit={handlePhoneSubmit} className="w-full space-y-4">
          <div>
            <label className="block text-xs font-semibold text-foreground/70 mb-1.5">
              Phone Number
              <span className="text-muted-foreground font-normal ml-1">/ No. Telefon</span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 013-525 6876"
              className="w-full border border-border rounded-xl px-4 py-3 text-[15px] bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[48px]"
              autoComplete="tel"
              required
            />
          </div>

          {error && <p className="text-destructive text-sm text-center">{error}</p>}

          <button
            type="submit"
            disabled={loading || !phone.trim()}
            className="w-full bg-blue-600 text-white py-3.5 rounded-[14px] text-[15px] font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-lg shadow-blue-600/30 min-h-[48px]"
          >
            {loading ? 'Sending...' : 'Track My Car'}
          </button>
        </form>
      </div>

      <p className="text-[11px] text-muted-foreground mt-8">Powered by BengkelOS</p>
    </div>
  );
}
